/**
 * User Form Component
 *
 * Form for creating or editing a user
 */

import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { UnifiedSearchableSelect } from '@tpct/admin-ui';
import type { User, UserFormData } from '@/users/src/types/user';
import { __ } from '@/hub-shared/utils/i18n';
import { createUser, updateUser, handleApiError } from '@/users/src/utils/api';
import {
  buildRoleSelectOptions,
  readRoleOptions,
  resolveDefaultNewUserRole,
} from '@/users/src/utils/roleSelectOptions';

type UserFormProps = {
  user?: User | null;
  onSave: () => void;
  onCancel: () => void;
  bootstrapKey?: string;
};

const UserForm = ({
  user,
  onSave,
  onCancel,
  bootstrapKey = 'AccountingHubUsersAdmin',
}: UserFormProps) => {
  const isEdit = !!user;
  const roles = readRoleOptions(bootstrapKey);
  const defaultRole = resolveDefaultNewUserRole(roles);
  const roleOptions = buildRoleSelectOptions(bootstrapKey);
  const [formData, setFormData] = useState<UserFormData>({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    role: user?.role || defaultRole,
    company_ids: user?.company_id ? [user.company_id] : [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit && user) {
        await updateUser(user.id, formData, bootstrapKey);
      } else {
        await createUser(formData, bootstrapKey);
      }
      onSave();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="user-form container-style">
      <h2>{isEdit ? __("Modifier l'utilisateur") : __('Nouvel utilisateur')}</h2>

      {error && (
        <div className="notice notice-error">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="first_name">
              {__('Prénom')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-field">
            <label htmlFor="last_name">
              {__('Nom')} <span className="required">*</span>
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="email">
            {__('Email')} <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="form-field">
          <label htmlFor="role">
            {__('Rôle')} <span className="required">*</span>
          </label>
          <UnifiedSearchableSelect
            id="role"
            options={roleOptions}
            value={formData.role}
            onChange={(value) => {
              setFormData((prev) => ({
                ...prev,
                role: value,
              }));
            }}
            disabled={loading}
            searchable={false}
            placeholder={__('Sélectionner un rôle')}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {__('Annuler')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
          >
            {loading ? __('Enregistrement...') : isEdit ? __('Enregistrer') : __('Créer')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;

